import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { TP_COLORS } from '../../../constants';
import { IScheduleShortEvent } from '../../../store/V2/ScheduleSlice';
import { IUser } from '../../../store/UserSlice';
import { checkUserPermission } from '../../../helper';
import { USER_PERMISSION_INDIVIDUALS } from '../../../store/V2/RolesSlice';

interface UpcomingClassBannerProps {
  open: boolean;
  onClose: () => void;
  classData: IScheduleShortEvent;
  user: IUser | undefined;
}

const bannerStyles = {
  minHeight: { xs: '84px', sm: '60px' },
  display: 'flex',
  alignItems: 'center',
  position: 'sticky',
  top: { xs: '56px', sm: '64px' },
  padding: '8px 16px',
  zIndex: 5,
  background: {
    xs: `url('${process.env.PUBLIC_URL}/images/backgrounds/home-banner-left.png') top left/cover no-repeat, #1E293B`,
    sm: `url('${process.env.PUBLIC_URL}/images/backgrounds/home-banner-left.png') top left/contain no-repeat, url('${process.env.PUBLIC_URL}/images/backgrounds/home-banner-right.png') top right/contain no-repeat, #1E293B`,
  },
};

const UpcomingClassBanner: FunctionComponent<UpcomingClassBannerProps> = ({
  open,
  onClose,
  classData,
  user,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [isFirstTimeUpdated, setIsFirstTimeUpdated] = useState(false);
  const [isOngoingClass, setIsOngoingClass] = useState(false);
  const [humanizedDuration, setHumanizedDuration] = useState('');

  useEffect(() => {
    const classStartTime = moment(classData.start);
    const classEndTime = moment(classData.end);

    const updateCountdown = () => {
      const now = moment();
      const diffInSeconds = classStartTime.diff(now, 'seconds');
      const diffInEndSeconds = classEndTime.diff(now, 'seconds');

      if (diffInSeconds <= 0) {
        setIsOngoingClass(true);
        setIsFirstTimeUpdated(true);
      } else {
        const momentDuration = moment.duration(diffInSeconds, 'seconds');
        setSeconds(momentDuration.seconds());
        setMinutes(momentDuration.minutes());
        setHumanizedDuration(momentDuration.humanize(true));
        setIsFirstTimeUpdated(true);
      }
      if (diffInEndSeconds <= 0) {
        clearInterval(intervalId);
        setIsFirstTimeUpdated(false);
      }
    };

    const intervalId = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalId);
  }, [classData.start]);

  const { course } = classData;
  const isStudent = checkUserPermission(user, {
    headerContents: [
      USER_PERMISSION_INDIVIDUALS.HEADER_CONTENTS_DISCOUNT_BANNER,
    ],
  });
  const tutorFirstName = classData.tutors[0].tutor.firstname;
  const tutorLastName = classData.tutors[0].tutor.lastname;

  const classStartMoment = moment(classData.start);
  const now = moment();
  const timeDiff = classStartMoment.diff(now, 'seconds');
  const isWithin14Days = timeDiff <= 14 * 24 * 60 * 60 && timeDiff > 0;
  const isWithin10Minutes = timeDiff <= 10 * 60;

  if ((isWithin14Days || isOngoingClass) && isFirstTimeUpdated && open) {
    const isFinalCountdown = isWithin10Minutes;

    let bannerText = '';
    if (isOngoingClass) {
      bannerText = isStudent
        ? `Your Class with ${tutorFirstName} ${tutorLastName} (${classData.subject}) is ongoing`
        : `Your ${classData.subject} class is ongoing`;
    } else if (isFinalCountdown) {
      bannerText = isStudent
        ? `Your Class with ${tutorFirstName} ${tutorLastName} (${classData.subject}) will start in: `
        : `Your ${classData.subject} class will start in: `;
    } else {
      bannerText = `Your next ${classData.subject} class is `;
    }

    return (
      <Grid container sx={bannerStyles}>
        <>
          <Grid
            item
            xs={10}
            style={{
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center',
              paddingLeft: !belowMd ? '14%' : '',
              flexDirection: belowMd ? 'column' : 'row',
            }}
          >
            <Typography
              variant="subtitle1"
              color={TP_COLORS.white}
              textAlign="center"
            >
              {bannerText}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                mt: belowMd ? 1 : 0,
              }}
            >
              {!isOngoingClass && (
                <Box
                  padding="1px 8px"
                  bgcolor="#FFFFFF1A"
                  color={TP_COLORS.white}
                  borderRadius={1}
                  ml={belowMd ? 0 : '5px'}
                  mr={belowMd ? 2 : 0}
                >
                  <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>
                    {isFinalCountdown
                      ? `${minutes < 10 ? `0${minutes}` : minutes} : ${
                          seconds < 10 ? `0${seconds}` : seconds
                        }`
                      : humanizedDuration}
                  </Typography>
                </Box>
              )}

              <Button
                size="small"
                color="inherit"
                variant="contained"
                sx={{
                  color: TP_COLORS.black,
                  backgroundColor: TP_COLORS.white,
                  fontWeight: '600',
                  ml: belowMd ? 0 : '25px',
                }}
                onClick={() => {
                  const baseUrl = `${process.env.PUBLIC_URL}/portal/courses/${course?._id}`;
                  const scrollParam = `scroll=${classData._id}`;
                  const noAnimParam = `noanim=true`;

                  const fullUrl = isFinalCountdown
                    ? `${baseUrl}?${scrollParam}`
                    : `${baseUrl}?${scrollParam}&${noAnimParam}`;

                  navigate(fullUrl);
                }}
              >
                {isFinalCountdown ? 'Go to Class' : 'Check Details'}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={2} style={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              sx={{
                position: 'absolute',
                right: '20px',
                color: 'white',
              }}
              onClick={onClose}
            >
              <CloseIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </Grid>
        </>
      </Grid>
    );
  }
  return null;
};

export default UpcomingClassBanner;
